import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PurchaseButton = ({ 
  finalPrice = 0,
  isAvailable = true,
  walletBalance = 0,
  onPurchase = () => {},
  className = ''
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [showWalletModal, setShowWalletModal] = useState(false);
  
  const hasEnoughBalance = walletBalance >= finalPrice;
  const needsTopUp = !hasEnoughBalance;
  const shortfall = needsTopUp ? finalPrice - walletBalance : 0;

  const handlePurchaseClick = async () => {
    if (!isAvailable) return;
    
    if (needsTopUp) {
      setShowWalletModal(true);
      return;
    }

    setIsProcessing(true);
    try {
      await onPurchase();
    } catch (error) {
      console.error('Purchase failed:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleTopUp = () => {
    setShowWalletModal(false);
    // Navigate to wallet top-up
    window.location.href = '/university-wallet';
  };

  const getButtonText = () => {
    if (!isAvailable) return 'No disponible';
    if (isProcessing) return 'Procesando...';
    if (needsTopUp) return 'Añadir fondos y comprar';
    return 'Comprar ahora';
  };

  const getButtonVariant = () => {
    if (!isAvailable) return 'outline';
    if (needsTopUp) return 'warning';
    return 'default';
  };

  return (
    <>
      <div className={`space-y-4 ${className}`}>
        {/* Wallet Balance Display */}
        <div className="bg-muted/50 p-4 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Saldo disponible:</span>
            <div className="flex items-center space-x-2">
              <Icon name="Wallet" size={16} color="var(--color-success)" />
              <span className="font-medium text-foreground">
                ${walletBalance?.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN
              </span>
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Precio total:</span>
            <span className="font-semibold text-lg text-foreground">
              ${finalPrice?.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN
            </span>
          </div>

          {needsTopUp && (
            <div className="mt-3 p-3 bg-warning/10 border border-warning/20 rounded-md">
              <div className="flex items-center space-x-2">
                <Icon name="AlertTriangle" size={16} color="var(--color-warning)" />
                <span className="text-sm font-medium text-warning">
                  Fondos insuficientes
                </span>
              </div>
              <p className="text-sm text-warning/80 mt-1">
                Necesitas ${shortfall?.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN adicionales
              </p>
            </div>
          )}
        </div>

        {/* Purchase Button */}
        <Button
          variant={getButtonVariant()}
          size="lg"
          fullWidth
          disabled={!isAvailable || isProcessing}
          loading={isProcessing}
          onClick={handlePurchaseClick}
          iconName={needsTopUp ? 'Plus' : 'ShoppingBag'}
          iconPosition="left"
        >
          {getButtonText()}
        </Button>

        {/* Additional Info */}
        <div className="text-center space-y-2">
          <p className="text-xs text-muted-foreground">
            Al comprar, aceptas nuestros términos y condiciones
          </p>
          
          {isAvailable && (
            <div className="flex items-center justify-center space-x-4 text-xs text-muted-foreground">
              <div className="flex items-center space-x-1">
                <Icon name="Shield" size={12} />
                <span>Compra segura</span>
              </div>
              <div className="flex items-center space-x-1">
                <Icon name="RefreshCw" size={12} />
                <span>Cancelación gratuita</span>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Wallet Top-up Modal */}
      {showWalletModal && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-background rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold text-lg">Fondos Insuficientes</h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowWalletModal(false)}
              >
                <Icon name="X" size={20} />
              </Button>
            </div>

            <div className="space-y-4">
              <div className="bg-muted/50 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Saldo actual:</span>
                  <span className="font-medium text-foreground">
                    ${walletBalance?.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN
                  </span>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Precio total:</span>
                  <span className="font-medium text-foreground">
                    ${finalPrice?.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN
                  </span>
                </div>
                <div className="border-t border-border pt-2 mt-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-foreground">Necesitas añadir:</span>
                    <span className="font-semibold text-warning">
                      ${shortfall?.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <Button
                  variant="default"
                  fullWidth
                  onClick={handleTopUp}
                  iconName="Plus"
                  iconPosition="left"
                >
                  Añadir fondos a mi billetera
                </Button>
                
                <Button
                  variant="outline"
                  fullWidth
                  onClick={() => setShowWalletModal(false)}
                >
                  Cancelar
                </Button>
              </div>

              <p className="text-xs text-muted-foreground text-center">
                Serás redirigido a tu billetera universitaria para añadir fondos de forma segura.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PurchaseButton;